import {
  FirestoreCollections,
  ID,
  IDPrefixes,
  NewSubTask,
  NewTask,
} from '@griply/types';
import { createPrefixId, withRoles } from '@griply/utils';

import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';
import { buildTaskDocument } from './build-task-document';

// A tag (or other) relationship to link the new task to. Written in the SAME
// batch as the task so the first Firestore snapshot already shows the task
// linked — otherwise the list animates it from the unlinked bucket into its
// group. `existingReferences` is the relationship's current references when it
// already exists (→ update), or null when it must be created.
export type TaskRelationshipLink = {
  id: ID;
  existingReferences: ID[] | null;
};

export type CreateTaskWithSubTasksOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TTimestamp = unknown,
  TDb = FirestoreInstance,
> = {
  task: NewTask;
  subTasks: NewSubTask[];
  relationshipLinks: TaskRelationshipLink[];
  userId: ID;
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (
    collectionRef: NoInfer<TCollectionRef>,
    ...rest: string[]
  ) => TDocRef & { id: ID };
  setDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<string, unknown>,
  ) => Promise<void> | void;
  db: TDb;
  serverTimestamp: () => TFieldValue;
  timestampFromDate: (date: Date) => TTimestamp;
  writeBatch: (db: NoInfer<TDb>) => {
    set: (ref: NoInfer<TDocRef>, data: Record<string, unknown>) => void;
    update: (ref: NoInfer<TDocRef>, data: Record<string, unknown>) => void;
    commit: () => Promise<void>;
  };
  onWriteError: (error: unknown) => void;
};

export const createTaskWithSubTasks = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TTimestamp,
  TDb = FirestoreInstance,
>(
  options: CreateTaskWithSubTasksOptions<
    TDocRef,
    TCollectionRef,
    TFieldValue,
    TTimestamp,
    TDb
  >,
): Promise<Record<string, unknown>> => {
  const {
    task,
    subTasks,
    relationshipLinks,
    userId,
    collection,
    doc,
    setDoc,
    db,
    serverTimestamp,
    timestampFromDate,
    writeBatch,
    onWriteError,
  } = options;

  const taskCollection = collection(db, FirestoreCollections.Tasks);
  const mainRef = doc(taskCollection);
  const mainTask = buildTaskDocument(
    task,
    mainRef,
    userId,
    serverTimestamp,
    timestampFromDate,
    task.parentIds ?? null,
  );

  // Single write, no need for a batch.
  if (!subTasks.length && !relationshipLinks.length) {
    detachWrite(setDoc(mainRef, mainTask), onWriteError);
    return mainTask;
  }

  const batch = writeBatch(db);

  if (subTasks.length) {
    const subTaskDocs = subTasks.map((subTask) => {
      const ref = doc(taskCollection);
      return {
        ref,
        data: buildTaskDocument(
          subTask as unknown as NewTask,
          ref,
          userId,
          serverTimestamp,
          timestampFromDate,
          [mainRef.id],
        ),
      };
    });
    mainTask.childIds = subTaskDocs.map(({ ref }) => ref.id);
    subTaskDocs.forEach(({ ref, data }) => batch.set(ref, data));
  }

  batch.set(mainRef, mainTask);

  if (relationshipLinks.length) {
    const relationshipsCollection = collection(
      db,
      FirestoreCollections.Relationships,
    );
    const taskReference = createPrefixId(mainRef.id, IDPrefixes.Task);

    relationshipLinks.forEach(({ id, existingReferences }) => {
      const ref = doc(relationshipsCollection, id);

      if (existingReferences) {
        batch.update(ref, {
          references: Array.from(
            new Set([...existingReferences, taskReference]),
          ),
          updatedAt: serverTimestamp(),
        });
        return;
      }

      batch.set(ref, {
        ...withRoles({ id, references: [taskReference] }, userId),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });
  }

  detachWrite(batch.commit(), onWriteError);
  return mainTask;
};
